/**
 * PAGAMENTO — três coisas, com três donos (decisão do gestor, 2D):
 *
 *  1. CONDIÇÃO (forma, prazo, parcelas): combinada no negócio. Comprador ou
 *     gestor lançam; viaja dentro da ficha.
 *  2. PAGAMENTO EFETIVADO (quitação) e comprovante: o gestor e, desde a R1A
 *     (06/10/2026), a Consulta — é «baixa». Vários por ficha (pagamento
 *     dividido pelo limite diário do banco). Errou? Anula com motivo e lança
 *     outro — nada se apaga. Cada baixa mostra quem lançou. Todos que veem a
 *     ficha veem se está pago e quanto falta (o comprador também vê o
 *     comprovante — a tela avisa ao anexar).
 *  3. COMISSÃO DO COMPRADOR (VOCÊ PAGA) por cabeça: só o gestor lança, depois
 *     do negócio; a Consulta vê; o comprador nem sabe que existe (a seção não
 *     aparece e o servidor nem manda o dado para o aparelho dele).
 *  4. 3B · COMISSÃO DO CLIENTE (VOCÊ RECEBE), uma por porteira de cliente: o
 *     cliente paga ao gestor, por cabeça da porteira dele. Mesmas regras.
 *  5. 3F · INTERMEDIAÇÃO: na porteira Cliente quem paga o vendedor é o cliente,
 *     direto. O «falta pagar» é só a sua parte (Revenda, Meu estoque, ou a
 *     ficha inteira sem porteira); a do cliente aparece «a cargo do cliente».
 *  6. 3F · RECEBIMENTO DA REVENDA: o comprador final paga o gestor, de uma vez
 *     ou em várias. Gestor e Consulta lançam e anulam (com motivo — baixa,
 *     R1A); o comprador nem recebe o dado. Comprovante opcional.
 *
 * R2B (revisão de 06/10/2026, números coerentes entre telas):
 *  - FICHA DE VENDA fala no sentido certo: «Falta receber», «Recebimentos»,
 *    «Comprador». A quitação de uma ficha de Venda é o recebimento. A ficha de
 *    Conferência não tem «a pagar» nem «a receber»;
 *  - as CONTAS DESTA FICHA (o que falta pagar e receber, conta a conta) saem
 *    de `contasDaFicha` — o mesmo número das Fichas, do Custo, da tela Contas,
 *    do Relatório e do Excel;
 *  - os dois nomes fixos da comissão; a do comprador pode ser de um CORRETOR e
 *    pode ser paga FORA do acerto; a do cliente ganha os RECEBIMENTOS (014).
 *    Ficha pesada pelo próprio gestor não oferece comissão do comprador;
 *  - pago a mais: aviso. Parcelas que não somam o devido: «refazer?». Com
 *    pagamento lançado, as parcelas travam (o pago cobre as parcelas pela
 *    ordem de vencimento — mexer nelas mudaria o que já está pago).
 *
 * Tudo funciona sem sinal: fica no aparelho e sobe no «Sincronizar».
 */
import { useState } from 'react'
import { abrirComprovante, ArquivoRecusado, comprovantesDe, guardarArquivo, usarComprovantesDoServidor } from '../dados/arquivos'
import { mudarFicha } from '../dados/armazem'
import {
  anexarNaQuitacao, anexarNoPagamentoComissao, anexarNoRecebimento, anexarNoRecebimentoComissao, anularPagamentoComissao, anularQuitacao,
  anularRecebimento, anularRecebimentoComissao, chaveDoCliente, comissaoDoCliente, lancarComissao, lancarComissaoCliente, lancarPagamentoComissao,
  lancarQuitacao, lancarRecebimento, lancarRecebimentoComissao, pagamentosDaComissao, quitacoesDa, recebimentosDaComissao, recebimentosDaPorteira,
  usarPagamentos, vendaDaPorteira,
} from '../dados/pagamentos'
import { contasPorPorteira, divisaoDoPagamento, temApartacao, porteirasDa } from '../dominio/destinos'
import { situacaoDoRecebimento } from '../dominio/revenda'
import { usarFerramenta, usarSessao } from '../dados/sessao'
import { pode } from '../dominio/permissoes'
import { calcular, centavos, comissaoTotal, situacaoDePagamento, valorDaComissao } from '../dominio/contas'
import { avisoDasParcelas, comissaoPagaFora, comissaoRecebida, contasDaFicha, NOMES_DAS_CONTAS, parcelasTravadas, resumirContas } from '../dominio/financeiro'
import { dataBR, hoje, lerNumero, novoId, paraCampo, paraCampoEmReais, quando, reais } from '../dominio/numeros'
import { NOME_COMISSAO_CLIENTE, NOME_COMISSAO_COMPRADOR, type Comprovante, type Ficha, type PagamentoDeComissao, type Pagamento as Parcela, type Quitacao, type Recebimento } from '../dominio/tipos'
import { avisar, Campo, confirmar, Dica, Escolha, pedirMotivo, Segmento, Tarja } from '../ui'
import { NotasLigadas } from './Notas'

const FORMAS: [string, string][] = [['', 'a combinar'], ['PIX/TED', 'PIX / TED'], ['Boleto', 'Boleto'], ['Cheque', 'Cheque'], ['Dinheiro', 'Dinheiro'], ['Outra', 'Outra']]

function maisDias(d: string, n: number): string {
  const [a, m, dd] = d.split('-').map(Number)
  const x = new Date(a!, m! - 1, dd! + n)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}

/** R2B · As palavras da ficha: na Venda o dinheiro ENTRA (recebe), na Compra SAI (paga). */
function palavras(venda: boolean) {
  return venda
    ? { pago: 'Recebido', falta: 'Falta receber', quitado: 'Recebido inteiro', efetivados: 'Recebimentos', lancar: '+ Lançar recebimento', valorPago: 'Valor recebido (R$)', pagoEm: 'Recebido em', etiquetaPaga: 'recebida', aMais: 'Recebido a mais', nenhum: 'Nenhum recebimento lançado ainda.' }
    : { pago: 'Pago', falta: 'Falta pagar', quitado: 'Quitado', efetivados: 'Pagamentos efetivados', lancar: '+ Lançar pagamento efetivado', valorPago: 'Valor pago (R$)', pagoEm: 'Pago em', etiquetaPaga: 'paga', aMais: 'Pago a mais', nenhum: 'Nenhum pagamento lançado ainda.' }
}

export function Pagamento({ ficha }: { ficha: Ficha }) {
  const sessao = usarSessao()
  const papel = sessao.tipo === 'dentro' ? sessao.eu.pessoa.papel : 'consulta'
  // 3E: a matriz única (src/dominio/permissoes.ts) — comissão, só o gestor;
  // R1A: o pagamento efetivado é «baixa» — gestor e Consulta
  const gestor = pode(papel, 'comissao')
  const daBaixa = pode(papel, 'quitacao')
  const veComissao = pode(papel, 'verComissao')
  const pg = usarPagamentos()
  const quitacoes = quitacoesDa(pg, ficha.id)
  const r = calcular(ficha)
  const venda = ficha.finalidade === 'Venda'
  const conferencia = ficha.finalidade === 'Conferência'
  const w = palavras(venda)
  // 3F: o gestor paga só a parte dele; a das porteiras Cliente (intermediação) o cliente paga direto
  const dv = divisaoDoPagamento(ficha)
  const devido = venda ? r.liquido : dv.doGestor
  const s = situacaoDePagamento(ficha, devido, quitacoes)
  const aMais = r.temPreco ? centavos(Math.max(0, s.pago - devido)) : 0
  const pendentes = new Set([...pg.pendentes.quitacoes, ...pg.pendentes.anulacoes])
  const verVenda = pode(papel, 'verVenda')
  // 3C: «Comissões e pagamentos» desligada esconde as comissões e os pagamentos
  // efetivados (e o «pago/falta pagar», que só existe com eles). A condição
  // combinada fica sempre: faz parte do negócio.
  const comComissoes = usarFerramenta('comissoes')
  // R2B: as contas desta ficha, o mesmo número de todas as telas
  const contas = contasDaFicha(ficha, pg, { verComissao: veComissao })
  const resumo = resumirContas(contas)

  return (
    <div>
      {conferencia && <Tarja tipo="ok">Ficha de Conferência: não entra no «a pagar» nem no «a receber».</Tarja>}
      <div className="resultado">
        <div className={`tl${comComissoes ? '' : ' total'}`}><span>Valor líquido do romaneio{venda ? ' (o comprador paga)' : ''}</span><b>{r.temPreco ? reais(r.liquido) : '—'}</b></div>
        {!venda && r.temPreco && dv.doCliente.map((c) => (
          <div className="tl" key={c.id}><span>A cargo do cliente · {c.nome} (paga direto ao vendedor)</span><b>{reais(c.valor)}</b></div>
        ))}
        {!venda && r.temPreco && dv.aCargoDoCliente > 0 && <div className="tl"><span>Sua parte</span><b>{reais(dv.doGestor)}</b></div>}
        {comComissoes && !conferencia && <div className="tl"><span>{w.pago}{venda ? '' : ' (lançado pelo escritório)'}</span><b>{reais(s.pago)}</b></div>}
        {comComissoes && !conferencia && <div className="tl total"><span>{s.quitado ? w.quitado : w.falta}</span><b>{r.temPreco ? reais(s.aPagar) : '—'}</b></div>}
        {s.proximoVencimento && !conferencia && <div className="mini-dica">Próximo vencimento: {dataBR(s.proximoVencimento)}</div>}
      </div>
      {!r.temPreco && <Tarja>Sem preço na negociação: o app não sabe quanto é o total, mas registra o combinado{comComissoes ? ' e os pagamentos' : ''} assim mesmo.</Tarja>}
      {/* R2B: dinheiro acima do devido — alguém lançou duas vezes, ou o valor errado */}
      {comComissoes && aMais > 0 && <Tarja tipo="erro">{w.aMais} {reais(aMais)}: os lançamentos somam {reais(s.pago)} e o devido é {reais(devido)}. Confira e anule o que estiver errado.</Tarja>}

      {/* R2B: as contas desta ficha — o que as Fichas e a tela Contas somam */}
      {comComissoes && contas.length > 0 && (
        <>
          <h2 className="secao">Contas desta ficha</h2>
          <div className="cartao">
            {contas.map((c) => (
              <div className="tl" key={c.id}>
                <span>{c.sentido === 'pagar' ? 'Pagar' : 'Receber'} · {c.tipo === 'gasto' ? c.descricao : NOMES_DAS_CONTAS[c.tipo]}
                  {c.tipo !== 'gasto' || c.quem !== c.descricao.split(' (')[0] ? ` — ${c.quem}` : ''}</span>
                <b>{c.quitada ? <span className="etiqueta ok">{c.sentido === 'pagar' ? 'pago' : 'recebido'}</span> : reais(c.falta)}</b>
              </div>
            ))}
            <div className="tl total"><span>Falta pagar · falta receber</span><b>{reais(resumo.aPagar)} · {reais(resumo.aReceber)}</b></div>
            <div className="mini-dica">O mesmo número das Fichas, do Custo, da tela Contas e do Relatório. Fretes e gastos se marcam pagos na aba Custo.</div>
          </div>
        </>
      )}

      {comComissoes && veComissao && !conferencia && (
        <SecaoComissao key={pg.comissoes[ficha.id]?.lancadaEm ?? 'nova'} ficha={ficha} cab={r.cab} gestor={gestor} daBaixa={pode(papel, 'pagamentoComissao')}
          souGestor={sessao.tipo === 'dentro' && sessao.eu.pessoa.papel === 'gestor'} sigla={sessao.tipo === 'dentro' ? sessao.eu.pessoa.sigla : ''} />
      )}
      {comComissoes && veComissao && <ComissoesDosClientes ficha={ficha} gestor={gestor} daBaixa={pode(papel, 'recebimentoComissao')} />}

      {/* R1A: o comprador não reescreve ficha encerrada (o banco recusa) — só reabrindo */}
      {!conferencia && (
        <Condicao ficha={ficha} parcelas={s.parcelas}
          podeMudar={pode(papel, 'condicaoDePagamento', { encerrada: ficha.situacao === 'encerrada' }) && ficha.situacao !== 'cancelada'} gestor={daBaixa}
          valor={devido} doCliente={venda ? 0 : dv.aCargoDoCliente} venda={venda}
          travada={parcelasTravadas(ficha.id, quitacoes)} />
      )}

      {comComissoes && !conferencia && (
        <>
          <h2 className="secao">{w.efetivados}</h2>
          {!quitacoes.length && <div className="cartao vazio">{daBaixa ? w.nenhum : `${w.nenhum.replace('.', '')} pelo escritório.`}</div>}
          {quitacoes.map((q) => <LinhaDeQuitacao key={q.id} q={q} gestor={daBaixa} pendente={pendentes.has(q.id)} venda={venda} />)}
          {daBaixa && ficha.situacao !== 'cancelada' && <NovaQuitacao ficha={ficha} sugestao={s.aPagar} venda={venda} />}
          {!daBaixa && <Dica>O {venda ? 'recebimento' : 'pagamento efetivado'} e o comprovante são lançados pelo escritório. Depois do «Sincronizar», aparece aqui {venda ? 'o que foi recebido' : 'se está pago'} e quanto falta.</Dica>}
        </>
      )}

      {/* 3F: o que o comprador final pagou ao gestor — só gestor e Consulta */}
      {comComissoes && verVenda && <RecebimentosDaRevenda ficha={ficha} gestor={pode(papel, 'recebimento')} />}

      {/* D4: as notas fiscais desta ficha (registrar, nunca emitir) */}
      <NotasLigadas ligacao={{ tipo: 'ficha', id: ficha.id }} podeAdicionar={ficha.situacao !== 'excluida'} />
    </div>
  )
}

/* ---------- Comissão do comprador (você paga) — gestor lança; Consulta dá baixa; comprador não chega aqui ---------- */

/** A ficha foi pesada pelo próprio gestor? (então não há comissão de comprador — só a de um corretor) */
function pesadaPeloGestor(ficha: Ficha, souGestor: boolean, sigla: string): boolean {
  if (typeof ficha.pesadoPorGestor === 'boolean') return ficha.pesadoPorGestor
  // ficha antiga: o número leva a sigla de quem abriu
  return souGestor && !!sigla && ficha.numero.startsWith(`${sigla}-`)
}

function SecaoComissao({ ficha, cab, gestor, daBaixa, souGestor, sigla }: { ficha: Ficha; cab: number; gestor: boolean; daBaixa: boolean; souGestor: boolean; sigla: string }) {
  const pg = usarPagamentos()
  const c = pg.comissoes[ficha.id]
  const doGestor = pesadaPeloGestor(ficha, souGestor, sigla)
  const [valor, setValor] = useState(paraCampo(c?.porCabeca ?? null))
  const [obs, setObs] = useState(c?.obs ?? '')
  // R2B: para quem — o comprador que pesou (no acerto) ou um corretor (fora do acerto)
  const [para, setPara] = useState<'comprador' | 'corretor'>(c?.beneficiario || doGestor ? 'corretor' : 'comprador')
  const [corretor, setCorretor] = useState(c?.beneficiario ?? '')
  const pendenteEnvio = pg.pendentes.comissoes.includes(ficha.id)
  // paga ao comprador num acerto do extrato: o banco não deixa mudar o valor
  // (anular o acerto a solta), então a tela nem oferece a correção
  const travada = !!c?.acertoId
  const pagamentos = pagamentosDaComissao(pg, ficha.id)
  const pagoFora = comissaoPagaFora(pg, ficha.id)
  const vc = c ? valorDaComissao(c, cab) : null
  const falta = vc && !travada ? centavos(Math.max(0, vc.valor - pagoFora)) : 0

  function salvar() {
    const v = lerNumero(valor)
    if (v === null || v < 0) { window.alert('Informe o valor da comissão por cabeça (pode ser 0 se não houve comissão).'); return }
    const benef = para === 'corretor' ? corretor.trim() : ''
    if (para === 'corretor' && !benef) { window.alert('Escreva o nome do corretor.'); return }
    lancarComissao(ficha.id, centavos(v), obs.trim(), benef)
    avisar(c ? 'Comissão corrigida' : 'Comissão lançada')
  }

  return (
    <>
      <h2 className="secao">{NOME_COMISSAO_COMPRADOR}</h2>
      <div className="cartao">
        {!c && !doGestor && <Tarja>Comissão do comprador pendente{gestor ? ': lance o valor por cabeça quando fechar.' : '.'}</Tarja>}
        {doGestor && <div className="mini-dica">Ficha pesada por você: não há comissão de comprador. Se houve um corretor, lance a comissão no nome dele.</div>}
        {c && vc && (
          // R1A: o acerto usa as cabeças do dia em que a comissão foi lançada
          <div className="tl"><span>{reais(c.porCabeca)} por cabeça × {vc.cab} cab · para {c.beneficiario ? `o corretor ${c.beneficiario}` : `o comprador${ficha.pesadoPor ? ` ${ficha.pesadoPor}` : ''}`}</span><b>{reais(vc.valor)}</b></div>
        )}
        {c && c.cabecas != null && c.cabecas !== cab && (
          <div className="mini-dica">Lançada com {c.cabecas} cabeça(s); a ficha hoje tem {cab}. O acerto usa {c.cabecas}.
            {gestor && !travada ? ' Para valer o número de hoje, salve a comissão de novo.' : ''}</div>
        )}
        {c?.obs && (!gestor || travada) && <div className="mini-dica">{c.obs}</div>}
        {travada && <div className="mini-dica"><span className="etiqueta ok">acertada</span> Comissão já paga no acerto com o comprador — não muda mais.</div>}
        {c && !travada && vc && vc.valor > 0 && (
          <div className="tl"><span>{pagoFora > 0 ? `Pago fora do acerto ${reais(pagoFora)} · falta` : 'Falta pagar'}</span><b>{reais(falta)}</b></div>
        )}
        {gestor && !travada && (
          <>
            {!doGestor && (
              <Segmento<'comprador' | 'corretor'> valor={para} mudar={setPara}
                opcoes={[['comprador', `Comprador da ficha${ficha.pesadoPor ? ` (${ficha.pesadoPor})` : ''}`], ['corretor', 'Um corretor']]} />
            )}
            {para === 'corretor' && <Campo rotulo="Nome do corretor" valor={corretor} mudar={setCorretor} dica="ex.: Zé Corretor" />}
            <div className="linha">
              <Campo rotulo="Comissão por cabeça (R$)" valor={valor} mudar={setValor} modo="decimal" dica="ex.: 15" />
              <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: combinado na porteira" />
            </div>
            <button className="botao" onClick={salvar}>{c ? 'Salvar correção da comissão' : 'Lançar comissão'}</button>
          </>
        )}
        {pendenteEnvio && <div className="mini-dica">Guardada neste aparelho — vai no próximo Sincronizar.</div>}
        <div className="mini-dica">{c?.beneficiario || para === 'corretor'
          ? 'Comissão de corretor: paga fora do acerto do extrato — lance o pagamento aqui embaixo.'
          : 'O gestor paga ao comprador no acerto do extrato (ou fora dele, lançando o pagamento aqui — aí ela não entra no acerto).'}
          {' '}Só o gestor e a Consulta veem a comissão. O comprador não vê, nem no romaneio.</div>

        {/* R2B (014): a comissão paga fora do acerto — gestor e Consulta (baixa) */}
        {c && !travada && (
          <>
            {pagamentos.map((x) => (
              <LinhaDeBaixa key={x.id} valor={x.valor} data={x.pagoEm} obs={[x.obs, x.contaOrigem ? `conta ${x.contaOrigem}` : ''].filter(Boolean).join(' · ')}
                quem={x.lancadoPorNome} anuladoEm={x.anuladoEm} anuladoMotivo={x.anuladoMotivo} anuladoPor={x.anuladoPorNome} recusado={x.recusado}
                pendente={pg.pendentes.pagamentosComissao.includes(x.id) || pg.pendentes.anulacoesPagamentoComissao.includes(x.id)}
                deTipo="pagamento_comissao" id={x.id} comprovantes={x.comprovantes} rotulo="pago" podeMexer={daBaixa}
                anexar={(n) => anexarNoPagamentoComissao(x.id, n)} anular={(m) => anularPagamentoComissao(x.id, m)} />
            ))}
            {daBaixa && falta > 0 && ficha.situacao !== 'cancelada' && (
              <NovaBaixa rotuloBotao="+ Lançar pagamento da comissão" rotuloValor="Valor pago (R$)" rotuloData="Pago em" sugestao={falta} comConta
                lancar={(v, d, o, conta) => lancarPagamentoComissao({ fichaId: ficha.id, valor: v, pagoEm: d, obs: o, contaOrigem: conta } as Pick<PagamentoDeComissao, 'fichaId' | 'valor' | 'pagoEm' | 'obs' | 'contaOrigem'>)} />
            )}
          </>
        )}
      </div>
    </>
  )
}

/* ---------- 3B · Comissão do cliente (você recebe), uma por porteira de cliente ---------- */

function ComissoesDosClientes({ ficha, gestor, daBaixa }: { ficha: Ficha; gestor: boolean; daBaixa: boolean }) {
  const pg = usarPagamentos()
  const clientes = porteirasDa(ficha).filter((p) => p.tipo === 'cliente')
  if (!clientes.length || ficha.finalidade !== 'Compra') return null
  // as cabeças de cada porteira (a mesma conta do Custo)
  const ap = contasPorPorteira(ficha, { verComissao: true })
  const cabDe = (id: string) => (temApartacao(ficha) ? ap.porteiras.find((p) => p.id === id)?.cab ?? 0 : ap.resultado.cab)
  return (
    <>
      <h2 className="secao">{NOME_COMISSAO_CLIENTE}</h2>
      {clientes.map((p) => {
        const c = comissaoDoCliente(pg, ficha.id, p.id)
        return <ComissaoDeUmCliente key={p.id + (c?.lancadaEm ?? 'nova')} ficha={ficha} destinoId={p.id} nome={p.nome} cab={cabDe(p.id)} gestor={gestor} daBaixa={daBaixa} />
      })}
    </>
  )
}

function ComissaoDeUmCliente({ ficha, destinoId, nome, cab, gestor, daBaixa }: { ficha: Ficha; destinoId: string; nome: string; cab: number; gestor: boolean; daBaixa: boolean }) {
  const pg = usarPagamentos()
  const c = comissaoDoCliente(pg, ficha.id, destinoId)
  const [valor, setValor] = useState(paraCampo(c?.porCabeca ?? null))
  const [obs, setObs] = useState(c?.obs ?? '')
  const pendenteEnvio = pg.pendentes.comissoesCliente.includes(chaveDoCliente(ficha.id, destinoId))
  const total = c ? comissaoTotal(c.porCabeca, cab) : 0
  const recebido = comissaoRecebida(pg, ficha.id, destinoId)
  const falta = centavos(Math.max(0, total - recebido))
  const recebimentos = recebimentosDaComissao(pg, ficha.id, destinoId)

  function salvar() {
    const v = lerNumero(valor)
    if (v === null || v < 0) { window.alert('Informe a comissão por cabeça que o cliente paga (pode ser 0 se não houver).'); return }
    lancarComissaoCliente(ficha.id, destinoId, centavos(v), obs.trim())
    avisar(c ? 'Comissão do cliente corrigida' : 'Comissão do cliente lançada')
  }

  return (
    <div className="cartao">
      <div className="item-topo"><b>{nome}</b><span className="etiqueta neutra">cliente</span></div>
      {!c && <Tarja>Comissão do cliente pendente{gestor ? ': lance o valor por cabeça combinado com o cliente.' : '.'}</Tarja>}
      {c && <div className="tl"><span>{reais(c.porCabeca)} por cabeça × {cab} cab</span><b>{reais(total)}</b></div>}
      {c && total > 0 && <div className="tl"><span>Recebido {reais(recebido)}</span>
        {falta > 0 ? <span className="etiqueta aberta">falta receber {reais(falta)}</span> : <span className="etiqueta ok">recebida</span>}</div>}
      {recebido > total && total > 0 && <Tarja tipo="erro">Recebido a mais {reais(centavos(recebido - total))}. Confira e anule o que estiver errado.</Tarja>}
      {c?.obs && !gestor && <div className="mini-dica">{c.obs}</div>}
      {gestor && (
        <>
          <div className="linha">
            <Campo rotulo="Comissão por cabeça (R$)" valor={valor} mudar={setValor} modo="decimal" dica="ex.: 25" />
            <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: combinado por telefone" />
          </div>
          <button className="botao fantasma" onClick={salvar}>{c ? 'Salvar correção' : 'Lançar comissão do cliente'}</button>
        </>
      )}
      {pendenteEnvio && <div className="mini-dica">Guardada neste aparelho — vai no próximo Sincronizar.</div>}
      {/* R2B (014): o que o cliente já pagou — gestor e Consulta dão baixa */}
      {recebimentos.map((x) => (
        <LinhaDeBaixa key={x.id} valor={x.valor} data={x.recebidoEm} obs={x.obs} quem={x.lancadoPorNome} anuladoEm={x.anuladoEm}
          anuladoMotivo={x.anuladoMotivo} anuladoPor={x.anuladoPorNome} recusado={x.recusado}
          pendente={pg.pendentes.recebimentosComissao.includes(x.id) || pg.pendentes.anulacoesRecebimentoComissao.includes(x.id)}
          deTipo="recebimento_comissao" id={x.id} comprovantes={x.comprovantes} rotulo="recebido" podeMexer={daBaixa}
          anexar={(n) => anexarNoRecebimentoComissao(x.id, n)} anular={(m) => anularRecebimentoComissao(x.id, m)} />
      ))}
      {daBaixa && c && falta > 0 && ficha.situacao !== 'cancelada' && (
        <NovaBaixa rotuloBotao="+ Lançar recebimento da comissão" rotuloValor="Valor recebido (R$)" rotuloData="Recebido em" sugestao={falta}
          lancar={(v, d, o) => lancarRecebimentoComissao({ fichaId: ficha.id, destinoId, valor: v, recebidoEm: d, obs: o })} />
      )}
      <div className="mini-dica">O cliente paga ao gestor: é receita. Só o gestor e a Consulta veem; o comprador não vê.</div>
    </div>
  )
}

/* ---------- R2B · baixa genérica (recebimento da comissão, pagamento da comissão) ---------- */

function NovaBaixa(p: {
  rotuloBotao: string; rotuloValor: string; rotuloData: string; sugestao: number; comConta?: boolean
  lancar: (valor: number, data: string, obs: string, conta: string) => void
}) {
  const [aberto, setAberto] = useState(false)
  const [valor, setValor] = useState('')
  const [em, setEm] = useState(hoje())
  const [obs, setObs] = useState('')
  const [conta, setConta] = useState('')
  if (!aberto) {
    return <button className="botao fantasma" onClick={() => { setValor(paraCampoEmReais(p.sugestao || null)); setEm(hoje()); setObs(''); setAberto(true) }}>{p.rotuloBotao}</button>
  }
  function lancar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor.'); return }
    if (!em) { window.alert('Informe a data.'); return }
    p.lancar(centavos(v), em, obs.trim(), conta.trim())
    avisar('Lançado — anexe o comprovante, se tiver')
    setAberto(false)
  }
  return (
    <div style={{ borderTop: '1px dashed var(--linha)', paddingTop: 8, marginTop: 8 }}>
      <div className="linha">
        <Campo rotulo={p.rotuloValor} valor={valor} mudar={setValor} modo="decimal" />
        <Campo rotulo={p.rotuloData} tipo="date" valor={em} mudar={setEm} />
      </div>
      <div className="linha">
        <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: PIX" />
        {p.comConta && <Campo rotulo="Conta de origem (opcional)" valor={conta} mudar={setConta} dica="ex.: Sicredi PJ" />}
      </div>
      <div className="barra">
        <button className="botao fantasma" onClick={() => setAberto(false)}>Desistir</button>
        <button className="botao" onClick={lancar}>Lançar</button>
      </div>
    </div>
  )
}

function LinhaDeBaixa(p: {
  valor: number; data: string; obs: string; quem?: string; anuladoEm: string | null; anuladoMotivo: string; anuladoPor?: string; recusado?: string
  pendente: boolean; deTipo: string; id: string; comprovantes: Comprovante[]; rotulo: string; podeMexer: boolean
  anexar: (novos: Comprovante[]) => void; anular: (motivo: string) => void
}) {
  const [guardando, setGuardando] = useState(false)
  const doServidor = usarComprovantesDoServidor()
  const comprovantes = comprovantesDe(doServidor, p.deTipo, p.id, p.comprovantes)
  const fora = !!p.anuladoEm || !!p.recusado
  async function anexar(arquivos: FileList | null) {
    if (!arquivos?.length) return
    setGuardando(true)
    const novos: Comprovante[] = []
    try {
      for (const a of Array.from(arquivos)) {
        const id = novoId()
        const guardado = await guardarArquivo(id, a)
        novos.push({ id, nome: a.name, tipo: guardado.type, tamanho: guardado.size })
      }
    } catch (e) {
      window.alert(e instanceof ArquivoRecusado ? e.message : 'Não foi possível guardar o arquivo neste aparelho.')
    } finally {
      if (novos.length) { p.anexar(novos); avisar('Comprovante anexado') }
      setGuardando(false)
    }
  }
  function anular() {
    const motivo = pedirMotivo(`Anular o lançamento de ${reais(p.valor)} de ${dataBR(p.data)}? Ele continua na lista, riscado.\n\nMotivo:`, true)
    if (motivo === null) return
    p.anular(motivo)
    avisar('Anulado')
  }
  return (
    <div className={`pagamento${fora ? ' anulado' : ' pago'}`} style={{ borderTop: '1px dashed var(--linha)', paddingTop: 8, marginTop: 8 }}>
      <div className="item-topo">
        <b className={fora ? 'riscado' : ''}>{reais(p.valor)} · {dataBR(p.data)}</b>
        <span>
          {p.recusado ? <span className="etiqueta cancelada">recusado pelo servidor</span>
            : p.anuladoEm ? <span className="etiqueta cancelada">anulado</span> : <span className="etiqueta ok">{p.rotulo}</span>}
          {p.pendente && <span className="etiqueta aberta">a enviar</span>}
        </span>
      </div>
      {p.obs && <div className="mini-dica">{p.obs}</div>}
      {p.quem && <div className="mini-dica">Lançado por {p.quem}</div>}
      {p.recusado && <div className="mini-dica">Não entrou no banco e não conta: {p.recusado}</div>}
      {p.anuladoEm && !p.recusado && <div className="mini-dica">Anulado {quando(p.anuladoEm)}{p.anuladoPor ? ` por ${p.anuladoPor}` : ''}: {p.anuladoMotivo}</div>}
      <Comprovantes lista={comprovantes} />
      {p.podeMexer && !fora && (
        <>
          <label className="botao fantasma pequeno anexar">
            {guardando ? 'Guardando…' : '📎 Anexar comprovante (foto ou PDF)'}
            <input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => { anexar(e.target.files); e.target.value = '' }} />
          </label>
          <button className="mini" onClick={anular}>Anular</button>
        </>
      )}
    </div>
  )
}

/* ---------- condição combinada: forma e parcelas ---------- */

function Condicao({ ficha, parcelas, podeMudar, gestor, valor, doCliente, venda, travada }: {
  ficha: Ficha; parcelas: { id: string; coberto: number; quitada: boolean }[]; podeMudar: boolean; gestor: boolean
  /** 3F · o que o gestor paga (sem a parte a cargo do cliente); na Venda, o que o comprador paga */
  valor: number; doCliente: number
  venda: boolean
  /** R2B · há pagamento lançado: as parcelas não mudam mais */
  travada: boolean
}) {
  const [quantas, setQuantas] = useState('1')
  const [cadaDias, setCadaDias] = useState('30')
  const forma = ficha.condicao?.forma ?? ''
  const r = calcular(ficha)
  const comPagamentos = usarFerramenta('comissoes')
  // R2B: com pagamento lançado, as parcelas travam (a forma e a observação continuam)
  const mudaParcelas = podeMudar && !(comPagamentos && travada)
  // R2B: «parcelas somam X, líquido é Y — refazer?»
  const aviso = r.temPreco ? avisoDasParcelas(ficha, valor) : null

  async function dividir() {
    const n = Math.max(1, Math.min(60, Math.round(lerNumero(quantas) ?? 1)))
    const dias = Math.max(0, Math.round(lerNumero(cadaDias) ?? 0))
    if (!r.temPreco) { window.alert('Sem preço na negociação o app não sabe o total. Lance as parcelas uma a uma.'); return }
    if (ficha.pagamentos.length && !await confirmar({ titulo: 'Trocar as parcelas combinadas pelas novas?', sim: 'Trocar' })) return
    // 3F: divide só a sua parte — a do cliente (intermediação) ele paga direto
    const cada = centavos(Math.floor((valor / n) * 100) / 100)
    mudarFicha(ficha.id, (f) => {
      // o centavo que sobra da divisão vai na última parcela, para a soma bater com o líquido
      f.pagamentos = Array.from({ length: n }, (_, i) => ({
        id: novoId(), valor: i === n - 1 ? centavos(valor - cada * (n - 1)) : cada,
        vencimento: maisDias(ficha.data || hoje(), dias * (i + 1)), obs: '',
      }))
    })
    avisar(n === 1 ? 'Parcela combinada' : `${n} parcelas combinadas`)
  }

  async function refazer() {
    if (!aviso) return
    if (!await confirmar({
      titulo: 'Refazer as parcelas?',
      texto: `As parcelas somam ${reais(aviso.soma)} e o ${venda ? 'líquido' : doCliente > 0 ? 'que você paga' : 'líquido'} é ${reais(aviso.devido)}. A última parcela fica com a diferença.`,
      sim: 'Refazer',
    })) return
    mudarFicha(ficha.id, (f) => {
      const ordem = f.pagamentos.map((p, i) => ({ p, i })).sort((a, b) => (a.p.vencimento || '9999').localeCompare(b.p.vencimento || '9999') || a.i - b.i)
      const ultima = ordem[ordem.length - 1]
      if (!ultima) return
      const outras = centavos(f.pagamentos.reduce((s, p, i) => s + (i === ultima.i ? 0 : p.valor), 0))
      const nova = centavos(valor - outras)
      if (nova > 0) f.pagamentos[ultima.i]!.valor = nova
    })
    avisar('Parcelas refeitas')
  }

  return (
    <>
      <h2 className="secao">Condição combinada</h2>
      <div className="cartao">
        <fieldset disabled={!podeMudar} className="sem-borda">
          <div className="linha">
            <Escolha rotulo="Forma de pagamento" valor={forma}
              opcoes={FORMAS.some(([v]) => v === forma) ? FORMAS : [...FORMAS, [forma, forma]]}
              mudar={(v) => mudarFicha(ficha.id, (f) => { f.condicao = { forma: v, obs: f.condicao?.obs ?? '' } })} />
            <CampoQueSalva key={ficha.condicao?.obs ?? ''} rotulo="Observação" valor={ficha.condicao?.obs ?? ''} dica="ex.: conta do filho"
              salvar={(v) => mudarFicha(ficha.id, (f) => { f.condicao = { forma: f.condicao?.forma ?? '', obs: v } })} />
          </div>
          {mudaParcelas && (
            <div className="linha fim">
              <Campo rotulo="Parcelas" valor={quantas} mudar={setQuantas} modo="numeric" />
              <Campo rotulo="A cada (dias)" valor={cadaDias} mudar={setCadaDias} modo="numeric" dica="0 = à vista" />
              <button type="button" className="mini alto" onClick={() => { void dividir() }}>{doCliente > 0 ? 'Dividir a sua parte' : 'Dividir o líquido'}</button>
            </div>
          )}
        </fieldset>
        {aviso && (
          <Tarja>Parcelas somam {reais(aviso.soma)}, {venda ? 'o líquido' : doCliente > 0 ? 'a sua parte' : 'o líquido'} é {reais(aviso.devido)}.
            {mudaParcelas && <> <button className="mini" onClick={() => { void refazer() }}>Refazer?</button></>}</Tarja>
        )}
        {podeMudar && comPagamentos && travada && <div className="mini-dica">Com {venda ? 'recebimento' : 'pagamento'} lançado, as parcelas ficam travadas: o {venda ? 'recebido' : 'pago'} cobre as parcelas pela ordem de vencimento. Para mudar, anule o lançamento.</div>}
        {!ficha.pagamentos.length && <div className="mini-dica">Nenhuma parcela combinada ainda.</div>}
        {ficha.pagamentos.map((p, i) => (
          <LinhaDeParcela key={p.id} ficha={ficha} p={p} n={i + 1} cobertura={parcelas[i]} podeMudar={mudaParcelas} gestor={gestor} venda={venda} />
        ))}
        {mudaParcelas && (
          <button type="button" className="botao fantasma pequeno" onClick={() => mudarFicha(ficha.id, (f) => {
            f.pagamentos.push({ id: novoId(), valor: 0, vencimento: hoje(), obs: '' })
          })}>+ Parcela</button>
        )}
      </div>
      <Dica>À vista: uma parcela. A prazo: a data de cada parcela.{comPagamentos ? ` O que foi ${venda ? 'recebido' : 'pago'} de fato é lançado pelo escritório, embaixo.` : ''}</Dica>
    </>
  )
}

function LinhaDeParcela({ ficha, p, n, cobertura, podeMudar, gestor, venda }: {
  ficha: Ficha; p: Parcela; n: number; cobertura?: { coberto: number; quitada: boolean }; podeMudar: boolean; gestor: boolean; venda: boolean
}) {
  // 3C: «paga/aberta» e a confirmação do pago antigo são dos pagamentos efetivados
  const comPagamentos = usarFerramenta('comissoes')
  const w = palavras(venda)
  const mudarParcela = (fn: (x: Parcela) => void) => mudarFicha(ficha.id, (f) => { const x = f.pagamentos.find((y) => y.id === p.id); if (x) fn(x) })
  const situacao = cobertura?.quitada && p.valor > 0
    ? <span className="etiqueta ok">{w.etiquetaPaga}</span>
    : cobertura && cobertura.coberto > 0
      ? <span className="etiqueta aberta">{w.etiquetaPaga} em parte · {reais(cobertura.coberto)}</span>
      : <span className="etiqueta neutra">aberta</span>

  // etapa 1: alguém anotou «pago» na própria parcela. Não conta — o gestor confirma.
  function confirmarAntigo() {
    if (!p.pagoEm) return
    if (!window.confirm(`Lançar como ${venda ? 'recebimento' : 'pagamento efetivado'}: ${reais(p.valor)} em ${dataBR(p.pagoEm)}?`)) return
    lancarQuitacao({ fichaId: ficha.id, parcelaId: p.id, valor: p.valor, pagoEm: p.pagoEm, obs: p.obs || 'anotado antes do banco', comprovantes: p.comprovantes ?? [] })
    mudarParcela((x) => { delete x.pagoEm; delete x.comprovantes })
    avisar('Lançado')
  }

  return (
    <div className={`pagamento${comPagamentos && cobertura?.quitada ? ' pago' : ''}`} style={{ borderTop: '1px dashed var(--linha)', paddingTop: 8, marginTop: 8 }}>
      <div className="item-topo"><b>{n}ª parcela</b>{comPagamentos && situacao}</div>
      <fieldset disabled={!podeMudar} className="sem-borda">
        <div className="linha">
          {/* R2B: o valor com separador de milhar (51.911,70) */}
          <CampoQueSalva key={p.valor} rotulo="Valor (R$)" valor={paraCampoEmReais(p.valor)} modo="decimal"
            salvar={(v) => { const x = lerNumero(v); if (x !== null && x >= 0) mudarParcela((y) => { y.valor = centavos(x) }) }} />
          <Campo rotulo="Vencimento" tipo="date" valor={p.vencimento} mudar={(v) => mudarParcela((x) => { x.vencimento = v })} />
        </div>
      </fieldset>
      {comPagamentos && p.pagoEm && (
        <Tarja>
          Anotado como {venda ? 'recebido' : 'pago'} em {dataBR(p.pagoEm)}, antes do banco. Não conta até o escritório confirmar.
          {gestor && <> <button className="mini" onClick={confirmarAntigo}>Confirmar</button></>}
        </Tarja>
      )}
      {!!p.comprovantes?.length && <Comprovantes lista={p.comprovantes} />}
      {podeMudar && !p.pagoEm && (
        <button type="button" className="mini" onClick={() => {
          if (window.confirm(`Tirar a ${n}ª parcela do combinado?`)) mudarFicha(ficha.id, (f) => { f.pagamentos = f.pagamentos.filter((y) => y.id !== p.id) })
        }}>Tirar parcela</button>
      )}
    </div>
  )
}

/* ---------- pagamentos efetivados (quitações) — na Venda, os recebimentos ---------- */

function NovaQuitacao({ ficha, sugestao, venda }: { ficha: Ficha; sugestao: number; venda: boolean }) {
  const [aberto, setAberto] = useState(false)
  const [valor, setValor] = useState('')
  const [pagoEm, setPagoEm] = useState(hoje())
  const [obs, setObs] = useState('')
  const [conta, setConta] = useState('')
  const w = palavras(venda)

  if (!aberto) {
    return (
      <button className="botao" onClick={() => { setValor(paraCampoEmReais(sugestao || null)); setPagoEm(hoje()); setObs(''); setAberto(true) }}>
        {w.lancar}</button>
    )
  }
  function lancar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor.'); return }
    if (!pagoEm) { window.alert('Informe a data.'); return }
    lancarQuitacao({ fichaId: ficha.id, parcelaId: '', valor: centavos(v), pagoEm, obs: obs.trim(), comprovantes: [], ...(conta.trim() ? { contaOrigem: conta.trim() } : {}) })
    avisar('Lançado — anexe o comprovante, se tiver')
    setAberto(false)
  }
  return (
    <div className="cartao">
      <div className="linha">
        <Campo rotulo={w.valorPago} valor={valor} mudar={setValor} modo="decimal" />
        <Campo rotulo={w.pagoEm} tipo="date" valor={pagoEm} mudar={setPagoEm} />
      </div>
      <div className="linha">
        <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: PIX, conta do filho" />
        {/* R2B (014): de que conta saiu (o limite diário do banco é por conta) */}
        <Campo rotulo={venda ? 'Conta que recebeu (opcional)' : 'Conta de origem (opcional)'} valor={conta} mudar={setConta} dica="ex.: Sicredi PJ" />
      </div>
      <div className="barra">
        <button className="botao fantasma" onClick={() => setAberto(false)}>Desistir</button>
        <button className="botao" onClick={lancar}>Lançar</button>
      </div>
      <div className="mini-dica">{venda ? 'Recebido em partes: um lançamento para cada vez, cada um com seu comprovante.' : 'Pago em partes pelo limite do banco: um lançamento para cada dia, cada um com seu comprovante. A tela Contas mostra o total pago hoje.'}</div>
    </div>
  )
}

function LinhaDeQuitacao({ q, gestor, pendente, venda }: { q: Quitacao; gestor: boolean; pendente: boolean; venda: boolean }) {
  const [guardando, setGuardando] = useState(false)
  // 2G: os anexados aqui mais os que outro aparelho do gestor já subiu
  const doServidor = usarComprovantesDoServidor()
  const comprovantes = comprovantesDe(doServidor, 'quitacao', q.id, q.comprovantes)
  const rotulo = venda ? 'recebido' : 'pago'

  async function anexar(arquivos: FileList | null) {
    if (!arquivos?.length) return
    setGuardando(true)
    const novos: Comprovante[] = []
    try {
      for (const a of Array.from(arquivos)) {
        const id = novoId()
        // a foto é guardada já comprimida: tipo e tamanho são os do guardado
        const guardado = await guardarArquivo(id, a)
        novos.push({ id, nome: a.name, tipo: guardado.type, tamanho: guardado.size })
      }
    } catch (e) {
      window.alert(e instanceof ArquivoRecusado ? e.message : 'Não foi possível guardar o arquivo neste aparelho.')
    } finally {
      if (novos.length) {
        anexarNaQuitacao(q.id, novos)
        avisar(novos.length === 1 ? 'Comprovante anexado' : `${novos.length} comprovantes anexados`)
      }
      setGuardando(false)
    }
  }

  function anular() {
    if (q.recusada) return
    const motivo = pedirMotivo(`Anular o ${venda ? 'recebimento' : 'pagamento'} de ${reais(q.valor)} de ${dataBR(q.pagoEm)}? Ele continua na lista, riscado.\n\nMotivo:`, true)
    if (motivo === null) return
    anularQuitacao(q.id, motivo)
    avisar('Anulado')
  }

  return (
    <div className={`cartao pagamento${q.anuladaEm || q.recusada ? ' anulado' : ' pago'}`}>
      <div className="item-topo">
        <b className={q.anuladaEm || q.recusada ? 'riscado' : ''}>{reais(q.valor)} · {dataBR(q.pagoEm)}</b>
        <span>
          {q.recusada ? <span className="etiqueta cancelada">recusado pelo servidor</span>
            : q.anuladaEm ? <span className="etiqueta cancelada">anulado</span> : <span className="etiqueta ok">{rotulo}</span>}
          {pendente && <span className="etiqueta aberta">a enviar</span>}
        </span>
      </div>
      {q.obs && <div className="mini-dica">{q.obs}</div>}
      {q.contaOrigem && <div className="mini-dica">{venda ? 'Conta que recebeu' : 'Conta de origem'}: {q.contaOrigem}</div>}
      {q.lancadaPorNome && <div className="mini-dica">Lançado por {q.lancadaPorNome}</div>}
      {q.recusada && <div className="mini-dica">Não entrou no banco e não conta como {rotulo}: {q.recusada} Fica só neste aparelho.</div>}
      {q.anuladaEm && !q.recusada && <div className="mini-dica">Anulado {quando(q.anuladaEm)}{q.anuladaPorNome ? ` por ${q.anuladaPorNome}` : ''}: {q.anuladaMotivo}</div>}
      {/* 2G: o comprador também abre o comprovante do pagamento da ficha dele (o banco confere) */}
      <Comprovantes lista={comprovantes} />
      {gestor && (
        <>
          {!q.anuladaEm && !q.recusada && (
            <>
              <label className="botao fantasma pequeno anexar">
                {guardando ? 'Guardando…' : '📎 Anexar comprovante (foto ou PDF)'}
                <input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => { anexar(e.target.files); e.target.value = '' }} />
              </label>
              {/* R1A: o comprovante do pagamento é anexado à ficha — e a ficha é do comprador */}
              <div className="mini-dica">O comprador desta ficha vê este arquivo.</div>
              <button className="mini" onClick={anular}>Anular {venda ? 'recebimento' : 'pagamento'}</button>
            </>
          )}
        </>
      )}
    </div>
  )
}

/* ---------- 3F · recebimento da revenda (gestor e Consulta dão baixa) ---------- */

function RecebimentosDaRevenda({ ficha, gestor }: { ficha: Ficha; gestor: boolean }) {
  const pg = usarPagamentos()
  const revendas = porteirasDa(ficha).filter((p) => p.tipo === 'revenda')
  if (!revendas.length || ficha.finalidade !== 'Compra') return null
  const ap = contasPorPorteira(ficha, { verComissao: true, vendaDe: (d) => vendaDaPorteira(pg, ficha.id, d) })
  const pendentes = new Set([...pg.pendentes.recebimentos, ...pg.pendentes.anulacoesRecebimento])
  return (
    <>
      <h2 className="secao">Recebimento da revenda</h2>
      {revendas.map((p) => {
        const conta = ap.porteiras.find((x) => x.id === p.id)
        const valor = conta?.venda?.valor ?? null
        const lista = recebimentosDaPorteira(pg, ficha.id, p.id)
        const sr = situacaoDoRecebimento(valor, lista)
        const aMais = valor !== null ? centavos(Math.max(0, sr.recebido - valor)) : 0
        return (
          <div className="cartao" key={p.id}>
            <div className="item-topo"><b>{p.nome || 'Comprador final'}</b><span className="etiqueta neutra">revenda</span></div>
            <div className="tl"><span>Venda ao comprador final</span><b>{valor === null ? '—' : reais(valor)}</b></div>
            <div className="tl"><span>Recebido</span><b>{reais(sr.recebido)}</b></div>
            {valor !== null && <div className="tl total"><span>{sr.quitado ? 'Recebido inteiro' : 'Falta receber'}</span><b>{reais(sr.aReceber)}</b></div>}
            {aMais > 0 && <Tarja tipo="erro">Recebido a mais {reais(aMais)}. Confira e anule o que estiver errado.</Tarja>}
            {valor === null && <div className="mini-dica">Sem preço de venda ainda: monte a tabela em Negociação › «Preço de venda».</div>}
            {lista.map((x) => <LinhaDeRecebimento key={x.id} x={x} gestor={gestor} pendente={pendentes.has(x.id)} />)}
            {gestor && ficha.situacao !== 'cancelada' && <NovoRecebimento fichaId={ficha.id} destinoId={p.id} sugestao={sr.aReceber} />}
            <div className="mini-dica">Só o gestor e a Consulta veem. O comprador não vê o preço de venda nem o que foi recebido.</div>
          </div>
        )
      })}
    </>
  )
}

function NovoRecebimento({ fichaId, destinoId, sugestao }: { fichaId: string; destinoId: string; sugestao: number }) {
  const [aberto, setAberto] = useState(false)
  const [valor, setValor] = useState('')
  const [em, setEm] = useState(hoje())
  const [obs, setObs] = useState('')
  if (!aberto) {
    return <button className="botao fantasma" onClick={() => { setValor(paraCampoEmReais(sugestao || null)); setEm(hoje()); setObs(''); setAberto(true) }}>+ Lançar recebimento</button>
  }
  function lancar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor recebido.'); return }
    if (!em) { window.alert('Informe a data do recebimento.'); return }
    lancarRecebimento({ fichaId, destinoId, valor: centavos(v), recebidoEm: em, obs: obs.trim() })
    avisar('Recebimento lançado — anexe o comprovante, se tiver')
    setAberto(false)
  }
  return (
    <div style={{ borderTop: '1px dashed var(--linha)', paddingTop: 8, marginTop: 8 }}>
      <div className="linha">
        <Campo rotulo="Valor recebido (R$)" valor={valor} mudar={setValor} modo="decimal" />
        <Campo rotulo="Recebido em" tipo="date" valor={em} mudar={setEm} />
      </div>
      <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: PIX do frigorífico" />
      <div className="barra">
        <button className="botao fantasma" onClick={() => setAberto(false)}>Desistir</button>
        <button className="botao" onClick={lancar}>Lançar</button>
      </div>
    </div>
  )
}

function LinhaDeRecebimento({ x, gestor, pendente }: { x: Recebimento; gestor: boolean; pendente: boolean }) {
  return (
    <LinhaDeBaixa valor={x.valor} data={x.recebidoEm} obs={x.obs} quem={x.lancadoPorNome} anuladoEm={x.anuladoEm} anuladoMotivo={x.anuladoMotivo}
      anuladoPor={x.anuladoPorNome} recusado={x.recusado} pendente={pendente} deTipo="recebimento" id={x.id} comprovantes={x.comprovantes}
      rotulo="recebido" podeMexer={gestor} anexar={(n) => anexarNoRecebimento(x.id, n)} anular={(m) => anularRecebimento(x.id, m)} />
  )
}

/* ---------- miudezas ---------- */

function Comprovantes({ lista }: { lista: Comprovante[] }) {
  if (!lista.length) return null
  return (
    <div className="comprovantes">
      {lista.map((c) => (
        <div key={c.id} className="comprovante">
          <button className="ligacao" onClick={async () => { const erro = await abrirComprovante(c.id); if (erro) window.alert(erro) }}>
            📎 {c.nome}</button>
        </div>
      ))}
    </div>
  )
}

/** Campo que só grava ao sair dele (valor digitado pela metade não vira alteração da ficha). */
function CampoQueSalva(p: { rotulo: string; valor: string; salvar: (v: string) => void; modo?: 'decimal'; dica?: string }) {
  const [v, setV] = useState(p.valor)
  return (
    <label className="campo"><span>{p.rotulo}</span>
      <input inputMode={p.modo} value={v} placeholder={p.dica} onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v.trim() !== p.valor) p.salvar(v.trim()); else setV(p.valor) }} />
    </label>
  )
}
